/**
 * domain-events.ts
 * Compras (REQUI) module — member schemas of the `POST /procurement/.../
 * events` discriminated unions (the module's ÚNICO mutation endpoint
 * pattern, SDD §11). One schema per `case_events.type` / SDD §4 transition
 * code, plus the three discriminated unions that compose them
 * (CaseEventInputSchema, PurchaseOrderEventInputSchema,
 * ConsolidationRoundEventInputSchema).
 *
 * refactor/compras-by-section — pure move out of index.ts (no shape/behavior change).
 */
import { z } from 'zod';

import { CancelReasonSchema, DeviationReasonSchema, JustificationReasonSchema, PurchaseOrderPaymentMethodSchema } from './shared.js';

// ---------------------------------------------------------------------------
// POST /procurement/cases/:id/events — the ÚNICO mutation endpoint's body.
// Discriminated union on `type`. Each entry names its SDD §4 transition code.
// ---------------------------------------------------------------------------

/** T0/T0b batch — "un solo evento requisicion_aprobada" (SDD §4). */
export const RequisicionAprobadaEventSchema = z.object({
  type: z.literal('requisicion_aprobada'),
  approvedItemIds: z.array(z.string().uuid()),
  excludedItems: z.array(z.object({ itemId: z.string().uuid(), motivo: CancelReasonSchema })),
});

/**
 * Rediseño de cotización (openspec/requi/rediseno-cotizacion.md §6.2) — quién
 * cotiza qué ítem, elegido por el comprador en el paso ②.1. `supplierIds`
 * nunca vacío: un ítem sin proveedor asignado simplemente no aparece acá
 * (queda pendiente, no bloquea).
 */
export const IniciarCotizacionAssignmentSchema = z.object({
  itemId: z.string().uuid(),
  supplierIds: z.array(z.string().uuid()).min(1),
});
export type IniciarCotizacionAssignment = z.infer<typeof IniciarCotizacionAssignmentSchema>;

/**
 * T1 — abre la cotización del caso. `assignments` (rediseño de cotización)
 * es OPCIONAL por ahora: mientras el paso ②.1 del frontend no esté wireado,
 * omitirlo conserva el comportamiento viejo (auto-split por rubro,
 * invariante 22 — DEROGADA, ver SDD-requi.md §10). Cuando esté wireado pasa
 * a mandarse siempre — el fallback se saca en una limpieza posterior.
 */
export const IniciarCotizacionEventSchema = z.object({
  type: z.literal('iniciar_cotizacion'),
  deadline: z.string().datetime(),
  assignments: z.array(IniciarCotizacionAssignmentSchema).min(1).optional(),
});

/** T2m — carga manual de cotización (canal B). */
export const CotizacionCargadaManualEventSchema = z.object({
  type: z.literal('cotizacion_cargada_manual'),
  itemId: z.string().uuid(),
  supplierId: z.string().uuid(),
  channel: z.enum(['email', 'whatsapp', 'pdf', 'telefono']),
  unitPrice: z.number().positive(),
  currency: z.string(),
  qtyOffered: z.number().positive().optional(),
  deliveryDays: z.number().int().nonnegative().optional(),
  validUntil: z.string().datetime().optional(),
});

/**
 * T4 — el proveedor (transcripto por el comprador) desestima el ítem.
 * `motivo` es el motivo del PROVEEDOR (ej. "sin stock", "fuera de rubro"), no
 * el del comprador — no comparte vocabulario con CancelReasonSchema (T0b/T13/
 * T17, motivo de CANCELACIÓN del comprador). Texto libre a propósito.
 *
 * `channel` — SDD §5: esta fila es parte de la MISMA carga manual (cabecera
 * canal+adjunto) que el resto de la grilla del round×proveedor; el schema
 * split de `quotation` (esta sesión) lo requiere para poder crear la
 * cabecera si esta es la primera llamada de la carga (proveedor que
 * desestimó TODOS los ítems de la ronda).
 */
export const ItemDesestimadoEventSchema = z.object({
  type: z.literal('item_desestimado'),
  itemId: z.string().uuid(),
  supplierId: z.string().uuid(),
  motivo: z.string().min(1),
  channel: z.enum(['email', 'whatsapp', 'pdf', 'telefono']),
});

/** T6 batch — cierra la ronda; cada ítem pasa a cotizado o desierto según cobertura. */
export const RondaCerradaEventSchema = z.object({
  type: z.literal('ronda_cerrada'),
  roundId: z.string().uuid(),
});

/** T9 — adjudicar. Dispara T14 (incluir_en_oc) automáticamente — agrupación de OCs. */
export const AdjudicarItemEventSchema = z.object({
  type: z.literal('adjudicar_item'),
  itemId: z.string().uuid(),
  quoteLineId: z.string().uuid(),
  deviationReason: DeviationReasonSchema.optional(),
  /** Optional free-text clarification, for ANY code (not just 'otro') — never mandatory. Stored in case_events.payload. */
  deviationNote: z.string().optional(),
});

/** T10 — adjudica una cantidad parcial; spawnea un ítem hijo por el saldo. */
export const AdjudicarCantidadParcialEventSchema = z.object({
  type: z.literal('adjudicar_cantidad_parcial'),
  itemId: z.string().uuid(),
  quoteLineId: z.string().uuid(),
  qtyAwarded: z.number().positive(),
  deviationReason: DeviationReasonSchema.optional(),
  deviationNote: z.string().optional(),
});

/** T13/T17 — cancela un ítem (desierto o pre-adjudicación), motivo obligatorio (código cerrado). */
export const CancelarItemEventSchema = z.object({
  type: z.literal('cancelar_item'),
  itemId: z.string().uuid(),
  motivo: CancelReasonSchema,
});

/**
 * T11 — un ítem desierto (nadie lo cotizó, o el proveedor avisó que ya no lo
 * tiene y se desestimó) vuelve a `pendiente` para salir a cotizar de nuevo
 * con otros proveedores, en un pedido nuevo (blueprint-compras.md §5 ②.3).
 */
export const ReabrirEnNuevaRondaEventSchema = z.object({
  type: z.literal('reabrir_en_nueva_ronda'),
  itemId: z.string().uuid(),
});

/**
 * T9 batch — flow-compras.md §3 "Adjudicar todo": adjudica de una todos los
 * ítems que este proveedor cotizó en la ronda, todo o nada. UN solo
 * deviationReason a nivel columna (no por ítem) — pedir uno por ítem haría
 * inusable el botón.
 */
export const AdjudicarColumnaEventSchema = z.object({
  type: z.literal('adjudicar_columna'),
  roundId: z.string().uuid(),
  supplierId: z.string().uuid(),
  deviationReason: DeviationReasonSchema.optional(),
  deviationNote: z.string().optional(),
});

/**
 * T14 batch — flow-compras.md §4 / blueprint-compras.md §5 "Generar órdenes
 * de compra": emite una OC por proveedor para TODOS los ítems 'adjudicado'
 * del caso, agrupando cruzando rubros/rondas. No lleva parámetros propios —
 * opera sobre el caso completo (caseId ya viene por la URL de /events).
 */
export const GenerarOrdenesCompraEventSchema = z.object({
  type: z.literal('generar_ordenes_compra'),
});

/**
 * T26 — revertir_adjudicacion: deshace T9 (o T10), vuelve el ítem a
 * 'cotizado'. Sin motivo obligatorio — corrección de rutina antes de que
 * exista la OC. Rechazada por el guard de dominio si el ítem fue adjudicado
 * por convenio (T19) o compra directa (T12) — no vinieron de una
 * quote_line — o si ya está en 'en_oc' (ahí se anula la línea, T18; no se
 * deshace la adjudicación).
 */
export const RevertirAdjudicacionEventSchema = z.object({
  type: z.literal('revertir_adjudicacion'),
  itemId: z.string().uuid(),
});

/**
 * T12 — compra_directa_justificada: un ítem `desierto` (salió a cotizar,
 * nadie cotizó válido) se adjudica DIRECTO a un proveedor elegido por el
 * comprador, sin pasar por una nueva ronda (SDD §4 invariante 8: "compra
 * directa exige justificación" — el guard de dominio rechaza sin ella).
 * `justificationReason` es JustificationReasonSchema (lista cerrada, un
 * clic) — ya NO texto libre. `justificationNote` solo se exige cuando
 * `justificationReason === 'otro'`; esa regla cruzada (un campo condiciona
 * a otro) no se puede expresar acá — z.discriminatedUnion (CaseEventInputSchema
 * más abajo) exige ZodObject planos, sin .refine() — se valida en
 * DirectPurchaseUseCase (mismo patrón que ActualizarOrdenCompraEventSchema).
 */
export const CompraDirectaJustificadaEventSchema = z.object({
  type: z.literal('compra_directa_justificada'),
  itemId: z.string().uuid(),
  supplierId: z.string().uuid(),
  unitPrice: z.number().positive(),
  currency: z.string(),
  justificationReason: JustificationReasonSchema,
  justificationNote: z.string().min(1).optional(),
  qtyOffered: z.number().positive().optional(),
  deliveryDays: z.number().int().nonnegative().optional(),
  validUntil: z.string().datetime().optional(),
});

/**
 * Sin código T — no es una transición de dominio (purchase_orders.status no
 * cambia, el domain engine no interviene). Blueprint §6: "N° de comprobante y
 * observación editables inline, como en el legacy". Semántica PATCH: una
 * clave ausente (undefined) no toca esa columna; `null` explícito la
 * vacía. Al menos una de las dos debe estar presente — validado en el use
 * case (no acá: z.discriminatedUnion exige ZodObject planos, sin .refine()).
 */
export const ActualizarOrdenCompraEventSchema = z.object({
  type: z.literal('orden_compra_actualizada'),
  purchaseOrderId: z.string().uuid(),
  receiptNumber: z.string().max(50).nullable().optional(),
  observation: z.string().max(2000).nullable().optional(),
  /** Wizard paso ④ mockup (migración 0150) — "Forma de pago". */
  paymentMethod: PurchaseOrderPaymentMethodSchema.nullable().optional(),
});

/**
 * Sin código T — no es una transición de dominio (case_items.status no
 * cambia, el domain engine/transition-engine.ts nunca ve este evento). Mismo
 * patrón que orden_compra_actualizada arriba: paso ① (blueprint-compras.md
 * §4) — "Rubro editable inline: el comprador clasifica acá lo que el
 * solicitante dejó vacío". Plain field update sobre case_items.category_id +
 * pendiente_normalizar, logueado como evento para el timeline como cualquier
 * otra mutación de /events (compras.routes.ts: "ÚNICO endpoint de
 * mutación", SDD §11) — deliberadamente NO una ruta REST aparte, para no
 * romper esa invariante.
 */
export const ItemClasificadoEventSchema = z.object({
  type: z.literal('item_clasificado'),
  itemId: z.string().uuid(),
  categoryId: z.string().uuid(),
});

/**
 * requi-consolicacion-multirubro (T9, 2026-09-26) — "ajustar_cantidad".
 * Sin código T — no es una transición de dominio (case_items.status no
 * cambia), mismo patrón que item_clasificado arriba: plain field update
 * sobre case_items.qty/qty_pendiente, logueado como evento. Solo mientras el
 * ítem está `pendiente` y no pooled (AdjustItemQuantityUseCase re-valida
 * ambas cosas server-side, nunca confía en lo que muestre la UI).
 */
export const AjustarCantidadEventSchema = z.object({
  type: z.literal('ajustar_cantidad'),
  itemId: z.string().uuid(),
  quantity: z.number().positive(),
  motivo: z.string().min(1),
});

/**
 * Sin código T — no es una transición de dominio (case_items.status no
 * cambia, el transition-engine nunca ve este evento). Rediseño de cotización
 * (openspec/requi/rediseno-cotizacion.md §6.7): "`+ proveedor` sobre una
 * ronda ya abierta" — agrega una invitación nueva a una ronda existente,
 * mismo patrón que item_clasificado/orden_compra_actualizada arriba.
 * `itemIds` ausente = todos los ítems ya presentes en la ronda (default del
 * blueprint §3.2).
 */
export const InvitacionAgregadaEventSchema = z.object({
  type: z.literal('invitacion_agregada'),
  roundId: z.string().uuid(),
  supplierId: z.string().uuid(),
  itemIds: z.array(z.string().uuid()).min(1).optional(),
});

/**
 * Inverso de invitacion_agregada — mismo posture (sin código T, el
 * transition-engine nunca lo ve). "Me equivoqué de proveedor": quita TODAS
 * las invitaciones del proveedor en la ronda, solo mientras la ronda siga
 * abierta y ese proveedor no tenga ninguna cotización cargada en ella.
 */
export const InvitacionQuitadaEventSchema = z.object({
  type: z.literal('invitacion_quitada'),
  roundId: z.string().uuid(),
  supplierId: z.string().uuid(),
});

/**
 * Sin código T — no es una transición de dominio (case_items.status no
 * cambia). Bug real, prod (2026-08-29): "en ningún lugar puedo editar
 * validez y la fecha... si no el comprador jamas se puede equivocar" —
 * create-or-reuse-quotation.ts freezes currency/deliveryDays/validityDate
 * the instant the first price is saved for a supplier's column
 * (CargaPreciosGrid.vue hid the editing controls once that happened). This
 * event corrects the cabecera of an ALREADY-EXISTING quotation while its
 * round is still open — same "editable mientras la ronda esté abierta" rule
 * SDD §5 already applies to per-item prices. Only the fields actually
 * provided get updated (server-side optional-field semantics, same as
 * CotizacionCargadaManualEventSchema's deliveryDays/validUntil).
 */
export const CotizacionCabeceraCorregidaEventSchema = z.object({
  type: z.literal('cotizacion_cabecera_corregida'),
  quotationId: z.string().uuid(),
  currency: z.string().optional(),
  deliveryDays: z.number().int().nonnegative().optional(),
  validUntil: z.string().datetime().optional(),
});

/**
 * T15 — oc_rechazada_por_proveedor. blueprint-compras.md §6/§7: el proveedor
 * rechaza la OC COMPLETA (no una línea) — sus ítems vuelven a `cotizado`.
 */
export const OcRechazadaPorProveedorEventSchema = z.object({
  type: z.literal('oc_rechazada_por_proveedor'),
  purchaseOrderId: z.string().uuid(),
});

/**
 * T16 — recepcion_registrada. `qty` es lo recibido EN ESTA entrega (delta),
 * no el acumulado — el use case lo suma contra `po_lines.qty_received` ya
 * persistido (blueprint §6: "estado de CADA LÍNEA", por ítem).
 *
 * `warehouseId` — requi-inventario-stock PR 2 (REQ-29.1/REQ-53.1) — depósito
 * destino del ingreso de stock. Obligatorio: una recepción sin depósito se
 * rechaza con 422 antes de escribir nada.
 */
export const RecepcionRegistradaEventSchema = z.object({
  type: z.literal('recepcion_registrada'),
  itemId: z.string().uuid(),
  qty: z.number().positive(),
  warehouseId: z.string().uuid(),
});

/**
 * T18 — anular_linea_oc. blueprint §6: "requiere doble confirmación" — el
 * gate HTTP exige `compras:approve-purchase` (ver AnnulPoLineUseCase.ts).
 */
export const AnularLineaOcEventSchema = z.object({
  type: z.literal('anular_linea_oc'),
  itemId: z.string().uuid(),
});

/**
 * T19 — adjudicar_contra_acuerdo: un ítem `pendiente` se adjudica DIRECTO
 * contra un acuerdo marco (framework_agreement) vigente para su rubro, sin
 * pasar por una ronda (SDD §4 invariante 12, revisado: vigencia NO bloquea —
 * un acuerdo vencido igual adjudica, con la marca no bloqueante
 * `acuerdo_vencido` en `case_items.deviation_flags`). `agreementId` identifica
 * el acuerdo elegido — `supplierId` NO viaja en el body: se resuelve del lado
 * del servidor a partir del acuerdo (AwardViaAgreementUseCase), para que el
 * cliente no pueda declarar un proveedor distinto del que el acuerdo cubre.
 */
export const AdjudicarContraAcuerdoEventSchema = z.object({
  type: z.literal('adjudicar_contra_acuerdo'),
  itemId: z.string().uuid(),
  agreementId: z.string().uuid(),
  unitPrice: z.number().positive(),
  currency: z.string(),
  qtyOffered: z.number().positive().optional(),
  deliveryDays: z.number().int().nonnegative().optional(),
  validUntil: z.string().datetime().optional(),
});

/**
 * Sin código T — no es una transición de dominio (purchase_orders.status no
 * cambia, el domain engine nunca ve este evento). Mockup de detalle de OC
 * (view_oc.png, decisión 2, 2026-08-23): "Solo marcar como enviada", SIN
 * integración real de envío. Mismo molde que orden_compra_actualizada.
 * Re-marcar una ya enviada es intencionalmente permitido (ver
 * MarkPurchaseOrderSentUseCase).
 */
export const OrdenCompraEnviadaEventSchema = z.object({
  type: z.literal('orden_compra_enviada'),
  purchaseOrderId: z.string().uuid(),
});

/**
 * Sin código T propio — requi-inventario-stock PR 3, "Abastecer desde pañol"
 * (blueprint-compras.md §5 ②.3, hermana de compra_directa_justificada/T12: la
 * misma fila ámbar de un ítem `desierto`, en vez de comprarlo a un proveedor,
 * el comprador cubre la necesidad con stock ya existente).
 *
 * NO se agregó a `ITEM_EVENTS`/`ItemEventSchema` (ver el header de esos
 * arrays: "one per row of SDD §4's TRANSITIONS table") — el motor de
 * transición nunca ve este literal. `SupplyFromWarehouseUseCase` internamente
 * dispara el MISMO evento de dominio que una cancelación (T13,
 * `event: 'cancelar_item'`, motivo cerrado `'hay_en_stock'` — ya existía en
 * `CANCEL_REASONS` antes de esta sesión, ver ese array) y persiste el
 * `case_events.type` resultante como `'cancelar_item'`, NO como
 * `'abastecido_desde_panol'` — mismo patrón de "evento HTTP propio, sin
 * código T, que dispara una transición YA existente" que
 * `item_clasificado`/`orden_compra_actualizada` usan para acciones que SÍ
 * mapean 1:1 a una transición nueva vs. ninguna. Además, atómicamente en la
 * MISMA transacción, emite el `egreso_consumo` correspondiente (mismo motor
 * `applyStockMovement`/repos que `RecordStockConsumptionUseCase`, imputado a
 * `caseId`/`caseItemId` — ver ese use case y su Schema arriba).
 */
export const AbastecerDesdePanolEventSchema = z.object({
  type: z.literal('abastecido_desde_panol'),
  itemId: z.string().uuid(),
  warehouseId: z.string().uuid(),
});

/**
 * 2026-08-31-requi-aprobacion-oc-por-monto — two-tier PO approval
 * (design.md "Interfaces / Contracts"). T27/T27e share the SAME event name
 * (`aprobar_oc_n1`), disambiguated by the domain engine's `matchesFrom` —
 * the existing T13/T17 precedent. `supplierId` (not `itemId`) because
 * approval acts on the whole `(caseId, supplierId)` group at once (D1/D3).
 *
 * Wired into `CaseEventInputSchema`'s discriminated union below as of Phase 4
 * (HTTP wiring) — `ApprovePoTierUseCase` (Phase 3) now backs all four cases
 * via `compras.routes.ts`'s `aprobar_oc_n1`/`rechazar_oc_n1`/`aprobar_oc_n2`/
 * `rechazar_oc_n2` event cases.
 */
export const AprobarOcN1EventSchema = z.object({
  type: z.literal('aprobar_oc_n1'),
  supplierId: z.string().uuid(),
});
export const AprobarOcN2EventSchema = z.object({
  type: z.literal('aprobar_oc_n2'),
  supplierId: z.string().uuid(),
});
export const RechazarOcN1EventSchema = z.object({
  type: z.literal('rechazar_oc_n1'),
  supplierId: z.string().uuid(),
  cancelReason: CancelReasonSchema,
});
export const RechazarOcN2EventSchema = z.object({
  type: z.literal('rechazar_oc_n2'),
  supplierId: z.string().uuid(),
  cancelReason: CancelReasonSchema,
});

export const CaseEventInputSchema = z.discriminatedUnion('type', [
  RequisicionAprobadaEventSchema,
  IniciarCotizacionEventSchema,
  CotizacionCargadaManualEventSchema,
  ItemDesestimadoEventSchema,
  RondaCerradaEventSchema,
  AdjudicarItemEventSchema,
  AdjudicarCantidadParcialEventSchema,
  CancelarItemEventSchema,
  ReabrirEnNuevaRondaEventSchema,
  AdjudicarColumnaEventSchema,
  GenerarOrdenesCompraEventSchema,
  RevertirAdjudicacionEventSchema,
  ActualizarOrdenCompraEventSchema,
  ItemClasificadoEventSchema,
  AjustarCantidadEventSchema,
  CompraDirectaJustificadaEventSchema,
  AdjudicarContraAcuerdoEventSchema,
  InvitacionAgregadaEventSchema,
  InvitacionQuitadaEventSchema,
  CotizacionCabeceraCorregidaEventSchema,
  OcRechazadaPorProveedorEventSchema,
  RecepcionRegistradaEventSchema,
  AnularLineaOcEventSchema,
  OrdenCompraEnviadaEventSchema,
  AbastecerDesdePanolEventSchema,
  AprobarOcN1EventSchema,
  AprobarOcN2EventSchema,
  RechazarOcN1EventSchema,
  RechazarOcN2EventSchema,
]);
export type CaseEventInput = z.infer<typeof CaseEventInputSchema>;

/**
 * requi-cotizacion-consolidada-por-rubro (Slice B, task 2.12, design.md D12)
 * — POST /procurement/purchase-orders/:id/events, the PO-scoped twin of the
 * case-scoped events route above. Only the 3 event types that are already
 * PO-scoped (never take a caseId) land here THIS slice: `orden_compra_
 * actualizada`, `orden_compra_enviada`, `oc_rechazada_por_proveedor`.
 * Reuses the exact same member schemas as CaseEventInputSchema — no new
 * shape, only a narrower closed union.
 */
export const PurchaseOrderEventInputSchema = z.discriminatedUnion('type', [
  ActualizarOrdenCompraEventSchema,
  OrdenCompraEnviadaEventSchema,
  OcRechazadaPorProveedorEventSchema,
]);
export type PurchaseOrderEventInput = z.infer<typeof PurchaseOrderEventInputSchema>;

/**
 * requi-cotizacion-consolidada-por-rubro (Slice C, task 3.16, design.md D8/
 * D12) — an HTTP COMMAND, not a `case_events.type`: what actually gets
 * persisted is N `cotizacion_cargada_manual` events, one per underlying
 * item (`RecordSummedLineQuoteUseCase`). No `qtyOffered` — this command
 * never prorates a partial quantity across underlying items.
 */
export const CotizarLineaSumadaEventSchema = z.object({
  type: z.literal('cotizar_linea_sumada'),
  itemIds: z.array(z.string().uuid()).min(1),
  supplierId: z.string().uuid(),
  unitPrice: z.number().positive(),
  currency: z.string(),
  channel: z.enum(['email', 'whatsapp', 'pdf', 'telefono']),
  deliveryDays: z.number().int().nonnegative().optional(),
  validUntil: z.string().datetime().optional(),
});

/**
 * requi-cotizacion-consolidada-por-rubro (Slice C, task 3.16, design.md D12)
 * — POST /procurement/consolidation-rounds/:id/events. Reuses the exact
 * member schemas already declared for `CaseEventInputSchema` (item-scoped +
 * round-scoped + approval subset D12 names) and adds the new
 * `cotizar_linea_sumada` command. Any other `type` fails closed (422) —
 * `generar_ordenes_compra` here dispatches to the CONSOLIDATED generation
 * use case, never the per-case one.
 */
export const ConsolidationRoundEventInputSchema = z.discriminatedUnion('type', [
  CotizacionCargadaManualEventSchema,
  ItemDesestimadoEventSchema,
  AdjudicarItemEventSchema,
  AdjudicarCantidadParcialEventSchema,
  RevertirAdjudicacionEventSchema,
  RondaCerradaEventSchema,
  AdjudicarColumnaEventSchema,
  InvitacionAgregadaEventSchema,
  InvitacionQuitadaEventSchema,
  CotizacionCabeceraCorregidaEventSchema,
  GenerarOrdenesCompraEventSchema,
  AprobarOcN1EventSchema,
  RechazarOcN1EventSchema,
  AprobarOcN2EventSchema,
  RechazarOcN2EventSchema,
  CotizarLineaSumadaEventSchema,
]);
export type ConsolidationRoundEventInput = z.infer<typeof ConsolidationRoundEventInputSchema>;
